import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, Paper, TextField, MenuItem, Grid, Chip, Button, Stack,
  CircularProgress, Snackbar, Alert, Divider, IconButton, Tooltip, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, ToggleButton, ToggleButtonGroup,
  Autocomplete,
} from '@mui/material';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import RefreshIcon from '@mui/icons-material/Refresh';
import PetsIcon from '@mui/icons-material/Pets';
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import PendingIcon from '@mui/icons-material/Pending';
import {
  collection, getDocs, doc, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { useBranch } from '../context/BranchContext';
import { COLORS } from '../theme/constants';
import { CLP, fmtFecha } from '../utils/finance';
import { addDaysLocal, todayLocalStr } from '../utils/dateUtils';

const { primary: PRIMARY, teal: TEAL, danger: DANGER, success: SUCCESS, amber: AMBER } = COLORS;

const addDays = (n) => addDaysLocal(n);
const todayStr = () => todayLocalStr();

// ── Helpers ───────────────────────────────────────────────────────────────────

// Determina si un presupuesto cumple la condición para admisión:
// estado === 'pagado' OR montoPagado >= 70% del total
const checkPresupuestoAdmission = (presupuesto) => {
  if (!presupuesto) return { ready: false, reason: 'Sin presupuesto asignado', pct: 0 };
  const total = Number(presupuesto.total) || 0;
  const pagado = Number(presupuesto.montoPagado) || 0;
  const pct = total > 0 ? Math.round((pagado / total) * 100) : 0;
  if (presupuesto.estado === 'pagado') return { ready: true, reason: 'Pagado', pct: 100 };
  if (pct >= 70) return { ready: true, reason: `Abono ${pct}%`, pct };
  return { ready: false, reason: `Abono ${pct}% (mín 70%)`, pct };
};

// ─── Component ────────────────────────────────────────────────────────────────
export default function HospitalAdmission() {
  const navigate = useNavigate();
  const { filterByBranch } = useBranch();

  const [orders, setOrders] = useState([]);
  const [presupuestos, setPresupuestos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('pendiente');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [assignDlg, setAssignDlg] = useState({ open: false, order: null });

  // ── Cargar órdenes de hospitalización y presupuestos ──
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [ordSnap, presSnap] = await Promise.all([
        getDocs(collection(db, 'hospitalizationOrders')),
        getDocs(collection(db, 'presupuestos')),
      ]);
      let ordList = ordSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      ordList = filterByBranch(ordList, { includeUnassigned: true });
      setOrders(ordList);

      const presList = presSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setPresupuestos(presList);
    } catch (err) {
      console.error('Error cargando datos de admisión:', err);
      setSnackbar({ open: true, message: 'Error al cargar la lista de admisión', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filterByBranch]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Mapa de presupuestos por id ──
  const presupuestosById = useMemo(() => {
    const m = {};
    presupuestos.forEach((p) => { m[p.id] = p; });
    return m;
  }, [presupuestos]);

  // ── Filtrar y ordenar ──
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return orders.filter((o) => {
      if (statusFilter && o.status !== statusFilter) return false;
      if (q && !(
        (o.patientName || '').toLowerCase().includes(q) ||
        (o.ownerName || '').toLowerCase().includes(q) ||
        (o.patientSpecies || '').toLowerCase().includes(q)
      )) return false;
      return true;
    }).sort((a, b) => {
      const da = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
      const db = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
      return db - da;
    });
  }, [orders, statusFilter, search]);

  // ── KPIs ──
  const counts = useMemo(() => {
    const c = { pendiente: 0, ingresado: 0, listoAdmision: 0 };
    orders.forEach((o) => {
      c[o.status] = (c[o.status] || 0) + 1;
      if (o.status === 'pendiente') {
        const pres = o.presupuestoId ? presupuestosById[o.presupuestoId] : null;
        const check = checkPresupuestoAdmission(pres);
        if (check.ready) c.listoAdmision++;
      }
    });
    return c;
  }, [orders, presupuestosById]);

  // ── Filtrar presupuestos del paciente para el diálogo de asignación ──
  const patientPresupuestos = useMemo(() => {
    if (!assignDlg.order) return [];
    const pid = assignDlg.order.patientId;
    return presupuestos
      .filter((p) => p.patientId === pid)
      .sort((a, b) => {
        const da = a.createdAt?.toDate ? a.createdAt.toDate() : 0;
        const db = b.createdAt?.toDate ? b.createdAt.toDate() : 0;
        return db - da;
      });
  }, [assignDlg, presupuestos]);

  // ── Asignar presupuesto a la orden ──
  const handleAssignPresupuesto = async (presupuestoId, vigenciaInicio, vigenciaFin) => {
    const order = assignDlg.order;
    if (!order || !presupuestoId) return;
    try {
      const pres = presupuestosById[presupuestoId];
      await updateDoc(doc(db, 'hospitalizationOrders', order.id), {
        presupuestoId,
        presupuestoNumber: pres?.numero || '',
        presupuestoVigenciaInicio: vigenciaInicio || null,
        presupuestoVigenciaFin: vigenciaFin || null,
        updatedAt: serverTimestamp(),
      });
      setSnackbar({ open: true, message: `Presupuesto ${pres?.numero || ''} asignado a ${order.patientName}`, severity: 'success' });
      setAssignDlg({ open: false, order: null });
      loadData();
    } catch (err) {
      setSnackbar({ open: true, message: `Error: ${err.message}`, severity: 'error' });
    }
  };

  return (
    <Box sx={{ bgcolor: '#F4F6F8', minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <LocalHospitalIcon sx={{ color: PRIMARY, fontSize: 32 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={800} color={PRIMARY}>Admisión Hospitalaria</Typography>
          <Typography variant="caption" color="text.secondary">
            Órdenes de hospitalización derivadas desde ficha de paciente — asignación de presupuesto
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData}>Actualizar</Button>
        <Button variant="contained" startIcon={<LocalHospitalIcon />} onClick={() => navigate('/hospital')}
          sx={{ bgcolor: PRIMARY, fontWeight: 700, '&:hover': { bgcolor: '#122a47' } }}>
          Centro de Control
        </Button>
      </Box>

      {/* KPIs */}
      <Stack direction="row" spacing={2} sx={{ mb: 2.5, flexWrap: 'wrap' }}>
        {[
          { label: 'Pendientes', value: counts.pendiente, color: AMBER },
          { label: 'Listas para admisión', value: counts.listoAdmision, color: SUCCESS },
          { label: 'Ingresadas', value: counts.ingresado, color: PRIMARY },
        ].map(({ label, value, color }) => (
          <Paper key={label} elevation={0} sx={{ px: 2, py: 1, border: '1px solid #E2E8F0', borderRadius: 2, minWidth: 140 }}>
            <Typography variant="h5" fontWeight={800} color={color}>{value}</Typography>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
          </Paper>
        ))}
      </Stack>

      {/* Filtros */}
      <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: 3, border: '1px solid #E2E8F0' }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <ToggleButtonGroup size="small" exclusive value={statusFilter}
            onChange={(_, v) => setStatusFilter(v)}>
            <ToggleButton value="pendiente">Pendientes</ToggleButton>
            <ToggleButton value="ingresado">Ingresadas</ToggleButton>
            <ToggleButton value="">Todas</ToggleButton>
          </ToggleButtonGroup>
          <TextField size="small" label="Buscar" placeholder="Paciente, tutor, especie…"
            value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 220 }} />
        </Stack>
      </Paper>

      {/* Lista */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : filtered.length === 0 ? (
        <Paper elevation={0} sx={{ p: 6, borderRadius: 3, border: '1px dashed #CBD5E1', textAlign: 'center' }}>
          <LocalHospitalIcon sx={{ fontSize: 56, color: '#CBD5E1' }} />
          <Typography color="text.disabled" mt={1}>No hay órdenes de hospitalización con estos filtros.</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filtered.map((o) => {
            const pres = o.presupuestoId ? presupuestosById[o.presupuestoId] : null;
            const check = checkPresupuestoAdmission(pres);
            const isPendiente = o.status === 'pendiente';
            return (
              <Grid key={o.id} size={{ xs: 12, md: 6, lg: 4 }}>
                <Paper elevation={0} sx={{
                  p: 2, borderRadius: 3, border: '1px solid #E2E8F0',
                  borderLeft: `4px solid ${check.ready ? SUCCESS : (pres ? AMBER : '#CBD5E1')}`,
                  height: '100%', display: 'flex', flexDirection: 'column',
                }}>
                  {/* Paciente */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Avatar sx={{ bgcolor: TEAL, width: 36, height: 36 }}><PetsIcon fontSize="small" /></Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight={700} noWrap>{o.patientName || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {o.patientSpecies || ''} {o.patientBreed ? `· ${o.patientBreed}` : ''}
                      </Typography>
                    </Box>
                    <Chip
                      label={o.status === 'pendiente' ? 'Pendiente' : 'Ingresado'}
                      size="small"
                      sx={{
                        bgcolor: o.status === 'pendiente' ? '#FEF3C7' : '#D1FAE5',
                        color: o.status === 'pendiente' ? '#92400E' : '#065F46',
                        fontWeight: 700, fontSize: '0.65rem',
                      }} />
                  </Box>

                  {/* Tutor */}
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                    Tutor: {o.ownerName || '—'}
                  </Typography>

                  {/* Datos clínicos */}
                  {o.formData?.motivoHospitalizacion && (
                    <Typography variant="body2" fontWeight={600} color={PRIMARY} sx={{ mb: 0.3 }}>
                      {o.formData.motivoHospitalizacion}
                    </Typography>
                  )}
                  {o.formData?.diagnosticoPresuntivo && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Dx: {o.formData.diagnosticoPresuntivo}
                    </Typography>
                  )}

                  {/* Presupuesto */}
                  <Divider sx={{ my: 1 }} />
                  {pres ? (
                    <Box>
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Chip
                          label={`Presupuesto ${pres.numero || ''}`}
                          size="small" variant="outlined"
                          sx={{ fontSize: '0.65rem' }} />
                        <Typography variant="caption" fontWeight={700} color="text.secondary">
                          {CLP(pres.total)}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }} useFlexGap>
                        <Chip
                          label={pres.estado || 'borrador'}
                          size="small"
                          sx={{
                            bgcolor: pres.estado === 'pagado' ? '#D1FAE5' : pres.estado === 'abonado' ? '#FEF3C7' : '#F1F5F9',
                            color: pres.estado === 'pagado' ? '#065F46' : pres.estado === 'abonado' ? '#92400E' : '#64748B',
                            fontSize: '0.65rem', fontWeight: 700,
                          }} />
                        {(pres.montoPagado > 0) && (
                          <Chip label={`Pagado: ${CLP(pres.montoPagado)}`} size="small" variant="outlined" sx={{ fontSize: '0.62rem' }} />
                        )}
                      </Stack>
                      {(o.presupuestoVigenciaInicio || o.presupuestoVigenciaFin) && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          Vigencia: {o.presupuestoVigenciaInicio || '—'} a {o.presupuestoVigenciaFin || '—'}
                        </Typography>
                      )}
                      {/* Estado de admisión */}
                      <Box sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: check.ready ? '#F0FDF4' : '#FFFBEB', border: `1px solid ${check.ready ? '#BBF7D0' : '#FDE68A'}` }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {check.ready
                            ? <CheckCircleIcon sx={{ fontSize: 16, color: SUCCESS }} />
                            : <WarningIcon sx={{ fontSize: 16, color: AMBER }} />}
                          <Typography variant="caption" fontWeight={700} sx={{ color: check.ready ? '#065F46' : '#92400E' }}>
                            {check.ready ? 'Lista para admisión' : check.reason}
                          </Typography>
                        </Stack>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ mt: 0.5, p: 1, borderRadius: 1, bgcolor: '#F8FAFC', border: '1px dashed #CBD5E1' }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <PendingIcon sx={{ fontSize: 16, color: '#94A3B8' }} />
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          Sin presupuesto asignado
                        </Typography>
                      </Stack>
                    </Box>
                  )}

                  {/* Acciones */}
                  <Box sx={{ mt: 'auto', pt: 1.5, display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                    {isPendiente && (
                      <Button
                        size="small" variant="outlined"
                        startIcon={<LinkIcon />}
                        onClick={() => setAssignDlg({ open: true, order: o })}
                        sx={{ fontSize: '0.72rem' }}>
                        {pres ? 'Cambiar presupuesto' : 'Asignar presupuesto'}
                      </Button>
                    )}
                    {pres && (
                      <Button size="small" variant="text"
                        onClick={() => navigate(`/presupuestos/${pres.id}`)}
                        sx={{ fontSize: '0.72rem' }}>
                        Ver presupuesto
                      </Button>
                    )}
                    {check.ready && isPendiente && (
                      <Button size="small" variant="contained"
                        startIcon={<LocalHospitalIcon />}
                        onClick={() => navigate('/hospital')}
                        sx={{ bgcolor: SUCCESS, fontSize: '0.72rem', fontWeight: 700, '&:hover': { bgcolor: '#059669' } }}>
                        Ingresar
                      </Button>
                    )}
                  </Box>

                  {/* Footer */}
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Deriva: {o.createdByName || '—'} · {fmtFecha(o.createdAt)}
                  </Typography>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ── Diálogo: Asignar presupuesto ── */}
      <AssignPresupuestoDialog
        open={assignDlg.open}
        order={assignDlg.order}
        presupuestos={patientPresupuestos}
        onClose={() => setAssignDlg({ open: false, order: null })}
        onAssign={handleAssignPresupuesto}
      />

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ─── Diálogo: Asignar presupuesto + vigencia ──────────────────────────────────
const AssignPresupuestoDialog = ({ open, order, presupuestos, onClose, onAssign }) => {
  const [selectedPres, setSelectedPres] = useState(null);
  const [vigenciaInicio, setVigenciaInicio] = useState(todayStr());
  const [vigenciaFin, setVigenciaFin] = useState(addDays(30));

  useEffect(() => {
    if (open) {
      setSelectedPres(null);
      setVigenciaInicio(todayStr());
      setVigenciaFin(addDays(30));
    }
  }, [open]);

  const handleConfirm = () => {
    if (!selectedPres) return;
    onAssign(selectedPres.id, vigenciaInicio, vigenciaFin);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: PRIMARY }}>
        Asignar presupuesto — {order?.patientName || ''}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Selecciona un presupuesto previamente creado para este paciente e indica la vigencia.
        </Typography>
        {presupuestos.length === 0 ? (
          <Paper elevation={0} sx={{ p: 3, textAlign: 'center', borderRadius: 2, border: '1px dashed #CBD5E1' }}>
            <Typography variant="body2" color="text.secondary">
              No hay presupuestos creados para este paciente.
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Crea un presupuesto desde la sección Recepción y Caja → Presupuestos.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={2}>
            <Autocomplete
              options={presupuestos}
              getOptionLabel={(p) => `${p.numero || 'S/N'} · ${CLP(p.total)} · ${p.estado || 'borrador'}`}
              renderOption={(props, p) => (
                <Box component="li" {...props}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={700}>{p.numero || 'S/N'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {CLP(p.total)} · Estado: {p.estado || 'borrador'}
                      {p.montoPagado > 0 ? ` · Pagado: ${CLP(p.montoPagado)}` : ''}
                    </Typography>
                  </Box>
                </Box>
              )}
              value={selectedPres}
              onChange={(_, v) => setSelectedPres(v)}
              renderInput={(p) => <TextField {...p} label="Presupuesto" size="small" fullWidth />}
            />
            {selectedPres && (
              <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Total</Typography>
                    <Typography variant="body2" fontWeight={700}>{CLP(selectedPres.total)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Pagado</Typography>
                    <Typography variant="body2" fontWeight={700} color={selectedPres.montoPagado > 0 ? SUCCESS : 'text.secondary'}>
                      {CLP(selectedPres.montoPagado || 0)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Saldo</Typography>
                    <Typography variant="body2" fontWeight={700} color={DANGER}>
                      {CLP((selectedPres.total || 0) - (selectedPres.montoPagado || 0))}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Estado</Typography>
                    <Typography variant="body2" fontWeight={700}>{selectedPres.estado || 'borrador'}</Typography>
                  </Box>
                </Stack>
              </Paper>
            )}
            <Stack direction="row" spacing={2}>
              <TextField
                size="small" type="date" label="Inicio vigencia" fullWidth
                InputLabelProps={{ shrink: true }}
                value={vigenciaInicio}
                onChange={(e) => setVigenciaInicio(e.target.value)} />
              <TextField
                size="small" type="date" label="Término vigencia" fullWidth
                InputLabelProps={{ shrink: true }}
                value={vigenciaFin}
                onChange={(e) => setVigenciaFin(e.target.value)} />
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={!selectedPres}
          sx={{ bgcolor: PRIMARY, fontWeight: 700, '&:hover': { bgcolor: '#122a47' } }}>
          Asignar presupuesto
        </Button>
      </DialogActions>
    </Dialog>
  );
};
